import React from "react";
import { Send } from "lucide-react";
import { Button } from "../../../components/ui";
import { formatDateTime } from "../clientMapping";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import type { PreconsultationFormSummary } from "../../preconsultation/services/preconsultationFormsService";

/**
 * The strip's ground and its border. Violet, like the buttons standing on it
 * and like nothing else in the app: the pre-consultation reaches out to a
 * client rather than changing anything in here, and the whole strip is that one
 * errand whichever of its three states it is in. Inline styles rather than
 * classes, the same bargain `SetupBanner` makes — they are one-off values, not
 * palette entries.
 */
const GROUND = "#F8F5FF";
const BORDER = "#CCADE8";

/**
 * Turned through half a circle: lucide draws the plane pointing up and to the
 * right, and this one flies the other way. Shared with the bare button the
 * page falls back to, so the two read as the same errand.
 */
export const sendIcon = (
  <Send size={16} aria-hidden="true" className="rotate-45" />
);

export interface PreconsultationBannerProps {
  /**
   * The client's first name, so the strip names who it is about — a screen
   * already titled with their full name does not need it twice.
   */
  firstName: string;
  /**
   * The form the screen speaks for, or `null` where there is none — see
   * `governingForm`, which is what decides between however many were raised.
   * `null` and a form in any state this strip has no words for both read as
   * nothing live.
   */
  form: PreconsultationFormSummary | null;
  /**
   * Whether one has ever been raised for this client. With no live form and
   * none ever sent, the strip is the card that explains what a
   * pre-consultation is; with none live but one sent before, the strip draws
   * nothing at all and the page puts a bare button in its header instead.
   */
  everSent: boolean;
  /** A link is being minted; the button that mints one waits on it. */
  sending: boolean;
  /** Mints the first link for a client who has never had one. */
  onSend: () => void;
  /** Opens the link already sent, to be handed to the client again. */
  onResend: () => void;
  /** Opens the submitted answers, where they are read and ruled on. */
  onView: () => void;
  /** Read once by the page and handed down; see `ClientDetailPage`. */
  gate: SubscriptionGate;
}

/** The shell all three states are drawn in, so they are one strip changing its mind. */
const Strip: React.FC<{
  title: string;
  detail: string;
  children: React.ReactNode;
}> = ({ title, detail, children }) => (
  <section
    style={{ backgroundColor: GROUND, borderColor: BORDER }}
    className="mt-5 flex-shrink-0 rounded-lg border px-4 py-4 sm:px-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-3"
  >
    <div className="min-w-0">
      <h2 className="text-[15px] sm:text-[17px] 3xl:text-[18px] font-semibold text-ink">
        {title}
      </h2>
      <p className="mt-1 text-[12px] sm:text-[13px] 3xl:text-[14px] text-[#5C5E6B]">
        {detail}
      </p>
    </div>

    {/* A line of its own on a phone, and only as wide as its buttons from
        `sm` up.

        `flex-shrink-0` used to be here at every width, which is what pushed
        this strip's buttons off the card: a flex item that may not shrink is
        laid out at its max-content width, so the `flex-wrap` on this very row
        never engaged and three buttons sized for a desktop simply ran past the
        right-hand edge. `w-full` below `sm` is the fix — the row takes the
        line, and what is in it decides how to share it. */}
    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-shrink-0 sm:gap-3">
      {children}
    </div>
  </section>
);

/**
 * The strip over the client's Details tab: where their pre-consultation stands,
 * and what there is to do about it.
 *
 * Four states, and each is a different errand:
 *
 *   never sent    — the health details a plan is built on are missing, and the
 *                   way to fill them in is to send the client a form. A card
 *                   rather than a bare button because that needs saying: a
 *                   practitioner looking at a row of dashes has no way of
 *                   knowing what fills them. Pressing it raises the link there
 *                   and then — there is nothing to confirm, because the client
 *                   holds no link that a new one could retire.
 *   sent, waiting — the ball is in the client's court, and the one thing to
 *                   do about it is send the link already raised out again.
 *   sent back     — answers are sitting there and nobody has ruled on them.
 *                   The strip says so and opens them; the decision itself is
 *                   made inside, against the whole submission rather than
 *                   against the two lines that fit on a strip.
 *   dealt with    — nothing. The card has said its piece once, and somebody
 *                   who has been through the errand does not need it explained
 *                   again, so the page carries the offer as a button beside the
 *                   client's name instead.
 *
 * It stands over the Details tab alone, because that is the tab the answers
 * land on: the row of dashes the card is explaining is right underneath it.
 * Gated like every other write: a workspace with no plan running cannot send a
 * form or rule on one.
 */
const PreconsultationBanner: React.FC<PreconsultationBannerProps> = ({
  firstName,
  form,
  everSent,
  sending,
  onSend,
  onResend,
  onView,
  gate,
}) => {
  if (form?.status === "SUBMITTED") {
    return (
      <Strip
        title={`${firstName} submitted the pre-consultation`}
        detail={`Submitted ${formatDateTime(
          form.submittedAt
        )}. Merge correct answers or return those needing fixes.`}
      >
        {/* One button, filled, and the only thing on the strip.

            Accept and Reject used to stand here beside it. Three buttons is
            more than a strip this size can hold — on a phone they ran past its
            edge — but the real objection is that two of them were asking for a
            decision from someone who had not read anything yet: the strip
            carries a name and a timestamp, and the answers being ruled on are
            behind the third button. So the pair has moved inside, under the
            submission they are about. See `PreconsultationReviewModal`.

            Which leaves reading the form as the strip's one errand, so it is
            drawn as one: filled, in the violet the strip is built in, rather
            than the quiet text button it was when it stood beside two louder
            things. Not ungated any more than it was — opening a form to read
            it changes nothing, and the gate is on the two buttons inside that
            do. */}
        <Button
          size="md"
          variant="inviteSolid"
          className="w-full sm:w-auto"
          onClick={onView}
        >
          View full form
        </Button>
      </Strip>
    );
  }

  if (form?.status === "PENDING") {
    return (
      <Strip
        title={`Waiting on ${firstName}'s health details`}
        detail={`Link shared on ${formatDateTime(form.createdAt)}`}
      >
        {/* One button, because there is one answer to an unread message:
            hand the client the link they already hold again. Minting a fresh
            one would retire theirs mid-form, and nothing here is worth that.
            It opens the same dialog the link was first handed over in, so the
            practitioner can put it on WhatsApp rather than only on the
            clipboard. */}
        <Button
          size="md"
          variant="inviteSolid"
          className="w-full sm:w-auto"
          onClick={onResend}
          endIcon={sendIcon}
          {...gate.props("send a pre-consultation form")}
        >
          Resend link
        </Button>
      </Strip>
    );
  }

  // Approved, returned or run out — and this client has been through it
  // before. The page draws the button; see `ClientDetailPage`.
  if (everSent) return null;

  return (
    <Strip
      title={`Collect ${firstName}'s health details first`}
      detail="Send a private pre-consultation form to gather height, goals, allergies and more — so the plan is built on real data."
    >
      <Button
        size="md"
        variant="inviteSolid"
        className="w-full sm:w-auto"
        onClick={onSend}
        loading={sending}
        endIcon={sendIcon}
        {...gate.props("send a pre-consultation form")}
      >
        Send pre-consultation
      </Button>
    </Strip>
  );
};

export default PreconsultationBanner;
